import { Injectable } from "@angular/core";
import { IsActiveMatchOptions } from "@angular/router";
import Swal from "sweetalert2/dist/sweetalert2.js";
import * as moment from "moment";
import { AbstractControl, FormControl, FormGroup, Validators } from "@angular/forms";

import { FormatDecimalsType } from "@core/services/utils/uitils.types";

export interface swalConfirm {
  text: string,
  title?: | '' | string,
  hTmL?: | false | boolean,
  buttonCancel?: | true | boolean,
  textButtonAceptar?: | 'Confirmar' | string,
  icon?: | 'question' | string,
}

@Injectable({ providedIn: "root", })
export class UtilsService {
  chartSeparator = "/-/";
  /**
   * Constructor
   */
  constructor() { }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get the equivalent "IsActiveMatchOptions" options for "exact = true".
   */
  get exactMatchOptions(): IsActiveMatchOptions {
    return {
      paths: "exact",
      fragment: "ignored",
      matrixParams: "ignored",
      queryParams: "exact",
    };
  }

  /**
   * Calcula cuántos días faltan para una fecha de vencimiento
   * @param expirationDate Fecha en formato "DD/MM/YYYY"
   * @returns Número de días por vencer o 'Vencido'
   */
  getDaysUntilExpiration(expirationDate: string): number | string {
    if (!expirationDate) {
      console.warn("No se recibió una fecha de vencimiento válida.");
      return "Fecha inválida";
    }

    // Convertir la fecha "DD/MM/YYYY" a un objeto moment
    const vencimiento = moment(expirationDate, "DD/MM/YYYY", true);

    if (!vencimiento.isValid()) {
      console.warn("Fecha inválida:", expirationDate);
      return "Fecha inválida";
    }

    // Calcular diferencia en días
    const today = moment().startOf("day");
    const diferenciaDias = vencimiento.diff(today, "days");

    return diferenciaDias >= 0 ? diferenciaDias : "Vencido";
  }

  /**
   * Formats a number into a currency string in the 'es-MX' locale (Mexican peso format),
   * with decimals limited to 6 digits.
   *
   * This method utilizes the `Intl.NumberFormat` API to format numbers as currency values. 
   * It specifically formats the value in the Mexican Peso (MXN) currency, displaying the 
   * appropriate thousand separators and decimal points, and ensures that up to 6 decimal
   * places are included in the formatted string.
   *
   * @param value - The numeric value to format.
   * @returns A string representing the formatted currency value in 'es-MX' locale.
   *
   * @example
   * const utilsService = new UtilsService();
   * const formattedValue = utilsService.formatCurrency(123456.78901234);
   * console.log(formattedValue); // Outputs: "$123,456.789012"
   *
   * Usage:
   * - Use this method whenever you need to display numbers as currency in the Mexican peso format.
   * - Example scenarios: financial reports, invoices, or price displays in e-commerce applications.
   */
  formatCurrency = (value: number | string, decimals: number = 2): string => {
    // Asegurarse de que el valor sea un número
    const numericValue = typeof value === 'number' ? value : Number(value);

    // Si el valor convertido no es un número, devuelve $0.00
    if (isNaN(numericValue)) {
      return '$0.00';
    }

    // Limitar a 6 dígitos decimales y formatear como moneda
    const roundedValue = Number(numericValue.toFixed(decimals));
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 2,
      maximumFractionDigits: 6,
    }).format(roundedValue);
  };

  calculateDates(daysDuration: number): { operationDate: string; settlementDate: string } {

    const operationDate = moment().format('YYYY/MM/DD');
    const settlementDate = moment()
      .add(daysDuration || 0, 'days')
      .format('YYYY/MM/DD'); // Fecha de operación + días de vigencia

    return { operationDate, settlementDate };
  }

  /**
   * Get the equivalent "IsActiveMatchOptions" options for "exact = false".
   */
  get subsetMatchOptions(): IsActiveMatchOptions {
    return {
      paths: "subset",
      fragment: "ignored",
      matrixParams: "ignored",
      queryParams: "subset",
    };
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------


  /**
   * !randomDate
   * @example randomDate(new Date(2024, 0, 1), new Date())
   * @param start
   * @param end
   * @returns
   */
  randomDate(start, end) {
    return new Date(
      start.getTime() + Math.random() * (end.getTime() - start.getTime())
    );
  }

  /**
   * ![randomTime]
   * @example randomTime(9, 12)
   * @returns
   */
  randomTime(hmin: number = 1, hmax: number = 24, _meridian: boolean = true) {
    let hrs = Math.round(Math.random() * (hmax - hmin) + hmin),
      mins = Math.round(Math.random() * 60),
      hFormat = hrs < 10 ? "0" : "",
      mFormat = mins < 10 ? "0" : "",
      amPm = hrs < 12 ? "AM" : "PM",
      is12 = hrs % 12 === 0;

    return amPm === "AM" && !is12
      ? `${hFormat}${hrs}:${mFormat}${mins} ${!_meridian ? "" : amPm}`
      : amPm === "AM" && is12
        ? `12:${mFormat}${mins} ${!_meridian ? "" : amPm}`
        : is12
          ? `${hFormat}${hrs}:${mFormat}${mins} ${!_meridian ? "" : amPm}`
          : `${hFormat}${hrs - 12}:${mFormat}${mins} ${!_meridian ? "" : amPm}`;
  }

  /**
   * Generates a random id
   *
   * @param _length
   */
  randomId(_length: number = 10, _numbers: boolean = false): string {
    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    const numbs = "0123456789";
    let theID = "";

    for (let i = 0; i < _length; i++) {
      theID += !_numbers
        ? chars.charAt(Math.floor(Math.random() * chars.length))
        : numbs.charAt(Math.floor(Math.random() * numbs.length));
    }

    return theID;
  }
  /**
   * Generates a random id
   *
   * @param _length
   */
  randomDecimal(
    _length: number = 10,
    _negative: boolean = false,
    _decimals: boolean = false
  ): string {
    const numbs = "0123456789";
    let theID = "";

    if (_negative) {
      for (let i = 0; i < _length; i++) {
        if (i % 2) {
          negative(i);
        } else {
          positive(i);
        }
      }
    } else {
      positive(_length);
    }

    function negative(_length) {
      for (let i = 0; i < _length; i++) {
        theID += "-" + numbs.charAt(Math.fround(Math.random() * numbs.length));
      }
    }

    function positive(_length) {
      for (let i = 0; i < _length; i++) {
        theID += numbs.charAt(Math.floor(Math.random() * numbs.length));
      }
    }

    return theID;
  }

  /**
   * !randomNumbers
   * @example randomNumbers(-2, 9, 2)
   * @param min
   * @param max
   * @param places  decimal places with integers.
   * @returns
   */
  randomNumbers(min, max, places) {
    // If both the minimum and maximum values are integers, return a random integer.
    // Don't let the user specify any decimal places.
    if (Number.isInteger(min) && Number.isInteger(max)) {
      if (places !== undefined) {
        new Error("Cannot specify decimal places with integers.");
      }
      return Math.floor(Math.random() * (max - min + 1)) + min;
    }

    // Otherwise, return a random floating point number with specified decimal places.
    else {
      // Make sure the minimum value is a number.
      if (Number.isNaN(Number.parseFloat(min))) {
        new Error("Minimum value is not a number.");
      }

      // Make sure the maximum value is a number.
      if (Number.isNaN(Number.parseFloat(max))) {
        new Error("Maximum value is not a number.");
      }

      // Make sure the decimal places value is a non-negative number greater than 0.
      if (Number.isInteger(places) === false) {
        new Error("Number of decimal places is not a number.");
      }

      if (places <= 0) {
        new Error("Number of decimal places must be at least 1.");
      }

      // Generate the floating point number.
      let value = Math.random() * (max - min + 1) + min;
      return Number.parseFloat(value).toFixed(places);
    }
  }

  setItmStorage(itmName, value) {
    if (typeof value === "object") value = JSON.stringify(value);
    sessionStorage.setItem(itmName, value);
    return true;
  }

  getItmStorage(itmName) {
    const value = sessionStorage.getItem(itmName);
    try {
      return JSON.parse(value);
    } catch (e) {
      return value;
    }
  }

  getArrayFromStorage(itmName) {
    let storageList = [],
      getStorageList = this.getItmStorage(itmName);

    if (typeof getStorageList === "object") {
      storageList.push(getStorageList);
    } else {
      let containStorageList = getStorageList.split(this.chartSeparator);
      containStorageList.forEach((itm) => {
        storageList.push(JSON.parse(itm));
      });
    }

    return storageList;
  }

  setArrayStorage(itmName, value) {
    let inStorage = sessionStorage.getItem(itmName);

    if (inStorage == undefined) {
      this.setItmStorage(itmName, value);
    } else {
      this.setItmStorage(
        itmName,
        inStorage + this.chartSeparator + JSON.stringify(value)
      );
    }
  }

  alertMessages(objAlert: swalConfirm) {
    Swal.fire({
      title: objAlert.title,
      text: objAlert.text,
      icon: objAlert.icon,
      allowEscapeKey: false,
      allowOutsideClick: false,
      confirmButtonText: "Aceptar"
    });
  }

  isEmpty(objectRecieve) {
    if (Array.isArray(objectRecieve)) {
      return objectRecieve.length == 0;
    }
    if (!objectRecieve) {
      return true;
    }
    if (objectRecieve instanceof Object) {
      if (objectRecieve instanceof Date) {
        return false;
      }
      if (Object.getOwnPropertyNames(objectRecieve).length == 0) {
        return true;
      }
    }

    return false;
  }



  /**
 * ![alertConfirm]
 * @param text 
 * @param title 
 * @param hTmL 
 * @param buttonCancel 
 * @param textButtonAceptar 
 * @returns 
 */


  alertConfirm(objConfrm: swalConfirm) {

    let text: any = objConfrm.text,
      title: string = objConfrm.title,
      hTmL: boolean | undefined = objConfrm.hTmL,
      buttonCancel: boolean | undefined = objConfrm.buttonCancel,
      textButtonAceptar: string = objConfrm.textButtonAceptar || 'Confirmar',
      icon: string | undefined = objConfrm.icon;

    return new Promise((reslvAlrtConf: any, rejctAlrtConf: any) => {
      Swal.fire({
        title: title,
        text: (hTmL === undefined) ? text : undefined,
        showDenyButton: (buttonCancel !== undefined) ? buttonCancel : true,
        showCancelButton: false,
        reverseButtons: true,
        allowEscapeKey: false,
        allowOutsideClick: false,
        html: (hTmL !== undefined) ? text : undefined,
        confirmButtonText: textButtonAceptar,
        denyButtonText: 'Cancelar',
        icon: (icon !== undefined) ? icon : undefined,
      }).then((result: any) => {
        /* Read more about isConfirmed, isDenied below */
        if (result.isConfirmed) {
          reslvAlrtConf(true)
        } else { rejctAlrtConf(false) }

      })
    })
  }

  /**
   * Valida si una contraseña contiene una secuencia de 3 o más caracteres
   * que sean: repetidos (aaa), consecutivos ascendentes (abc) o descendentes (cba).
   * aplica también para caracteres especiales situados de manera consecutiva en el teclado (!@#$%/) <-> (///) <-> (&^%)
   *
   * @param pass - Contraseña a validar.
   * @returns `true` si se detecta alguna secuencia inválida, `false` en caso contrario.
   */
  validaConsecutivos(pass: any): boolean {
    if (typeof pass === 'undefined') return false;

    // Normalizamos a minúsculas para comparar sin distinción de mayúsculas
    let pwd = (pass as string).toLowerCase();

    for (let i = 2; i < pwd.length; i++) {
      let prev2 = pwd.charCodeAt(i - 2),
        prev1 = pwd.charCodeAt(i - 1),
        curr = pwd.charCodeAt(i),
        sonRepetidos = prev2 === prev1 && prev1 === curr,          // ej: "aaa"
        sonAscendentes = prev2 + 1 === prev1 && prev1 + 1 === curr, // ej: "abc"
        sonDescendentes = prev2 - 1 === prev1 && prev1 - 1 === curr; // ej: "cba"

      if (sonRepetidos || sonAscendentes || sonDescendentes) {
        return true; // Se encontró una secuencia inválida, no es necesario continuar
      }
    }

    return false;
  }

  /**
   * 
   * @param textToConvert 
   * @returns 
   */
  convertToFloat(textToConvert: any) {
    return (textToConvert == (textToConvert * 1)) ? textToConvert * 1 : textToConvert
  }

  /**
   * !organizeNameFile
   * elimina acentos y espacios en blanco cambiándolos su carácter similar 
   * y en el caso de espacios en blanco usa guión bajo.
   *  */
  organizeNameFile(nameFile) {
    return nameFile.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, "").replace(/\s/g, "_")
  }

  /**
   * Genera un rango de números desde `start` hasta `end` (inclusivo).
   * @param start Número inicial.
   * @param end Número final.
   * @returns Array con los números del rango.
  */
  genRange(start: number, end: number): number[] {
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  /**
   * 
   * @param control 
   * @param existInForm 
   */
  setVldtrInFormGroup(formToValue: FormGroup, control: string, existInForm: boolean) {
    if (existInForm == true) {
      formToValue.controls[control].setValidators([Validators.required]);
      formToValue.get(control).updateValueAndValidity();
    } else {
      formToValue.get(control).clearValidators(); // Clear All Validators
      formToValue.get(control).updateValueAndValidity();
    }
  }


  /**
 * 
 * @param control 
 * @param existInForm 
 */
  setVldtrInFormControl(formToValue: FormControl, existInForm: boolean) {
    if (existInForm == true) {
      formToValue.setValidators([Validators.required]);
      formToValue.updateValueAndValidity();
    } else {
      formToValue.clearValidators(); // Clear All Validators
      formToValue.updateValueAndValidity();
    }
  }


  /**
* ![checkControlsInvalid]
* revisa los controles de un formulario
* devuelve cuáles están error y so motivo
*/
  checkControlsInvalid(controls: { [key: string]: AbstractControl; }) {
    for (let [keyForm, valueForm] of Object.entries(controls)) {
      if (valueForm.status === 'INVALID') {
        console.warn(keyForm, valueForm.value)
      console.warn(valueForm, '<-- value')
      } else { console.info('ok', keyForm) }
    }
  }



  /**
   * ![isUndefined]
   * @param variable 
   * @returns  {boolean}
   */
  isUndefined(variable) {
    return (variable === undefined && typeof variable === 'undefined' || variable === void 0) ? true : false;
  }

  /**
   * !getPercentOFValue
   * @param limitPercentInfSup [integer]  @example 5 for 5% comision or 16 for IVA
   * @param valueOfReference [integer] [value from which it is extracted] @exple price, amount or total
   * @returns   1% = (1/100)
   */
  getPercentOFValue(limitPercentInfSup, valueOfReference) {
    let limitPrcntInfSup = this.convertToFloat(limitPercentInfSup),
      valReference = this.convertToFloat(valueOfReference);

    return this.convertToFloat((limitPrcntInfSup * valReference) / (100))
  }

  /**
   * Formatea una fecha en formato 'dd/MM/yyyy' con la zona horaria de México.
   * @param dateStr Fecha en string (ISO o similar)
   * @returns Fecha formateada como string
   */
  formatDateOnly(dateStr: string): string {
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat('es-MX', {
      timeZone: 'America/Mexico_City',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(date);
  }

  /**
   * para saber cuántos decimales tiene un número
   * @param numberRecieve 
   * @returns 
   */
  countDecimal(numberRecieve) {
    return numberRecieve ? (numberRecieve.toString().match(/\.([0-9]+)/) || [, ''])[1].length : 0;
  }

  /**
   * Genera una lista de años desde el año actual menos uno hacia atrás.
   * @param numberOfYears Número de años a generar.
   * @returns Array de números representando los años.
  */
  getListOfYears(numberOfYears: number): number[] {
    const currentYear = new Date().getFullYear();
    const startYear = currentYear - numberOfYears;
    return Array.from({ length: currentYear - startYear }, (_, i) => startYear + i).sort((a, b) => b - a);
  }

  /**
   * !validContainBanco
   * @param passNew 
   * @returns 
   */
  validContainBanco(passNew: any) {
    let _contieneBanco =
      /(vepormas|bxmas|grupofinanciero|banco|casadebolsa|casabolsa|bolsa|casa|bx)/i.test(
        passNew.toLowerCase()
      );
    return _contieneBanco;
  }


  /**
   * !removeObjDuplicates
   * @param arr 
   * @param propertie 
   * @returns 
   */
  removeObjDuplicates(arr, propertie) {
    const seen = new Set();
    return arr.filter(obj => {
      if (seen.has(obj[propertie])) {
        return false; // si ya existe, lo descarta
      }
      seen.add(obj[propertie]);
      return true; // lo mantiene si no estaba repetido
    });
  };

  /**
   * mskContract
   * @param numContract 
   * @returns 
   */
  mskContract(numContract = '000') {
    return `****** ${numContract.slice(-3)}`
  }

  /**
   * !extractMailMasked
   * @param text [string] [text to extract the masked email]
   * @returns [string | null]
   */
  extractMailMasked(text: string): string | null {
    // Patrón para encontrar correos enmascarados con asteriscos
    const pattern = /\*+[a-zA-Z0-9]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/;
    let result = text.match(pattern);
    return result ? result[0] : null;
  }


  /**
   * 
   */
  firstMayus(input: string): string {
    if (typeof input !== 'string') { return input; }
    return input
      .toLowerCase()
      .replace(/(^|\.{3}\s+|\.\s+|:\s+)(\w)/g, (match, separator, letter) => {
        return separator + letter.toUpperCase();
      });
  }

  /**
   * 
   * @returns 
   */
  isSafari() {
    return /safari/i.test(navigator.userAgent) && !/chrome/i.test(navigator.userAgent);
  }

  /**
   * Formatea un valor numérico a una cantidad específica de decimales.
   * @param {FormatDecimalsType} configFormatNumber - Objeto de configuración con las propiedades:
   *   - valueToFormat: valor numérico a formatear
   *   - decimals: cantidad de decimales (por defecto: 2)
   *   - returnType: tipo de retorno 'string' o 'number' (por defecto: 'string')
   * @returns {number | string} Valor formateado según la configuración
   * @example
   * formatNumberToDecimals({ valueToFormat: 123456789.123456789 }) // "123456789.12"
   * formatNumberToDecimals({ valueToFormat: 123456789.123456789, decimals: 3, returnType: 'number' }) // 123456789.123
   */
  formatNumberToDecimals(configFormatNumber: FormatDecimalsType) {
    console.log(configFormatNumber, 'z--');
    const {
      valueToFormat: valueReceived, decimals = 2, returnType = 'string', isCurrency = false,
      currency = '$', thousandsSeparator = ',', decimalSeparator = '.'
    } = configFormatNumber;

    let numRecieve = parseFloat(valueReceived);
    if (isNaN(numRecieve)) return "Error: Valor no válido";

    let factor = Math.pow(10, decimals),
      numResult = Math.trunc(numRecieve * factor) / factor;

    if (returnType === 'number') return numResult;

    // Separamos parte entera y decimal
    let [integerPart, decimalPart] = numResult.toFixed(decimals).split('.'),
      // Aplicamos el separador de miles a la parte entera
      formattedInteger = integerPart.replace(
        /\B(?=(\d{3})+(?!\d))/g,
        thousandsSeparator
      );

    // Unimos con el separador decimal personalizado
    const formattedNumber = decimals > 0
      ? `${formattedInteger}${decimalSeparator}${decimalPart}`
      : formattedInteger;

    return isCurrency ? `${currency}${formattedNumber}` : formattedNumber;
  }

}


